import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ShieldCheck, ShieldOff, Download, Trash2, QrCode, KeyRound, CreditCard, Landmark, Building2, CheckCircle2, Link2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { useConfig } from "@/lib/useConfig";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BILLING_STATUS_META = {
  active: { key: "billing_status_active", color: "hsl(var(--success))" },
  trialing: { key: "billing_status_trialing", color: "hsl(var(--success))" },
  past_due: { key: "billing_status_past_due", color: "hsl(var(--warning))" },
  canceled: { key: "billing_status_canceled", color: "hsl(var(--destructive))" },
};

const CONNECT_STATUS_META = {
  not_started: { key: "connect_status_not_started", color: "hsl(var(--muted-foreground))" },
  onboarding: { key: "connect_status_onboarding", color: "hsl(var(--warning))" },
  restricted: { key: "connect_status_restricted", color: "hsl(var(--warning))" },
  active: { key: "connect_status_active", color: "hsl(var(--success))" },
};

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const config = useConfig();
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [connectBusy, setConnectBusy] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [billingBusy, setBillingBusy] = useState(null);
  const [billingInterval, setBillingInterval] = useState("month");
  const [cancelTrialOpen, setCancelTrialOpen] = useState(false);
  const [cancelSubOpen, setCancelSubOpen] = useState(false);
  const [nif, setNif] = useState(user?.nif || "");
  const [nifSaving, setNifSaving] = useState(false);

  const saveNif = async () => {
    setNifSaving(true);
    try {
      await api.put("/account/profile", { nif: nif || null });
      await refreshUser();
      toast.success("OK");
    } catch (e) {
      toast.error("Error");
    } finally {
      setNifSaving(false);
    }
  };

  const { data: plans } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });

  const { data: connectStatus } = useQuery({
    queryKey: ["rent-billing-connect-status"],
    queryFn: async () => (await api.get("/rent-billing/connect/status")).data,
    enabled: user?.role !== "accountant",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const connect = params.get("connect");
    if (checkout === "success") {
      toast.success(t("billing_checkout_success"));
      // Stripe's client-side redirect to success_url and its webhook delivery
      // are independent — the browser can land back here before the webhook has
      // activated the subscription. Poll briefly instead of trusting a single
      // refreshUser() to already reflect it, then drop the ?checkout=success
      // that's keeping App.js's subscription-wall guard open (stripping it any
      // earlier races the guard against the still-stale user). Once access is
      // confirmed, re-run whatever queries 403'd while it was still pending.
      (async () => {
        let fresh = await refreshUser();
        for (let attempt = 0; attempt < 5 && !fresh.has_app_access; attempt++) {
          await new Promise((r) => setTimeout(r, 1500));
          fresh = await refreshUser();
        }
        navigate("/settings", { replace: true });
        qc.invalidateQueries();
      })();
    } else if (checkout === "cancel") {
      toast.info(t("billing_checkout_cancel"));
      navigate("/settings", { replace: true });
    } else if (connect === "return" || connect === "refresh") {
      qc.invalidateQueries({ queryKey: ["rent-billing-connect-status"] });
      navigate("/settings", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConnectOnboarding = async () => {
    setConnectBusy(true);
    try {
      const res = await api.post("/rent-billing/connect");
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setConnectBusy(false);
    }
  };

  useEffect(() => {
    setNif(user?.nif || "");
  }, [user?.nif]);

  const startCheckout = async (planKey, checkoutInterval = "month") => {
    setBillingBusy(planKey);
    try {
      const res = await api.post("/billing/checkout", { plan: planKey, interval: checkoutInterval });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setBillingBusy(null);
    }
  };

  const openPortal = async () => {
    setBillingBusy("portal");
    try {
      const res = await api.post("/billing/portal");
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setBillingBusy(null);
    }
  };

  const cancelTrial = async () => {
    setBillingBusy("cancel-trial");
    try {
      await api.post("/billing/cancel-trial");
      await refreshUser();
      setCancelTrialOpen(false);
      toast.success(t("billing_cancel_trial_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBillingBusy(null);
    }
  };

  const cancelSubscription = async () => {
    setBillingBusy("cancel-sub");
    try {
      await api.post("/billing/cancel");
      await refreshUser();
      setCancelSubOpen(false);
      toast.success(t("billing_cancel_scheduled_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBillingBusy(null);
    }
  };

  const resumeSubscription = async () => {
    setBillingBusy("resume-sub");
    try {
      await api.post("/billing/resume");
      await refreshUser();
      toast.success(t("billing_resume_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBillingBusy(null);
    }
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/2fa/setup");
      setQr(res.data.qr);
      setSecret(res.data.secret);
      setSetupOpen(true);
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      await refreshUser();
      setSetupOpen(false);
      setCode("");
      toast.success("2FA ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      await refreshUser();
      setDisableCode("");
      toast.success("OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "yieldpilot-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("✓");
    } catch (e) {
      toast.error("Error");
    }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      toast.success(t("account_deleted"));
      logout();
      navigate("/login");
    } catch (e) {
      toast.error("Error");
    }
  };

  // Mirrors the backend's own checkout gate (billing.py create_checkout): a fresh
  // Checkout Session is rejected whenever this is true, so upgrade buttons should
  // not be offered either — the portal is the only correct path to switch plans.
  const hasActiveSubscription = !!user?.stripe_subscription_id && user?.billing_status !== "canceled";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-4xl">{t("nav_settings")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
      </div>

      {/* Billing (accountants are always on the free plan with no subscription) */}
      {user?.role !== "accountant" && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="p-6 rounded-sm border-border" data-testid="billing-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex gap-3">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-[hsl(var(--success))]" />
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  {t("billing_title")}
                  {user?.billing_status && user.billing_status !== "active" && (
                    <Badge variant="outline" className="rounded-sm text-[10px]"
                      style={{ color: BILLING_STATUS_META[user.billing_status]?.color, borderColor: BILLING_STATUS_META[user.billing_status]?.color }}>
                      {t(BILLING_STATUS_META[user.billing_status]?.key)}
                    </Badge>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">{t("billing_desc")}</p>
                <p className="text-sm mt-2">
                  <span className="text-muted-foreground">{t("billing_current_plan")}:</span>{" "}
                  <span className="font-medium">{user?.plan || "basic"}</span>
                </p>
                {config?.ai_quota_monthly > 0 && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="ai-usage-indicator">
                    {t("ai_usage_label")}: {config.ai_usage_this_month}/{config.ai_quota_monthly}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {user?.billing_status === "trialing" && (
                <Button variant="outline" onClick={() => setCancelTrialOpen(true)} disabled={!!billingBusy}
                  data-testid="cancel-trial-btn"
                  className="rounded-sm gap-2 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10">
                  {t("billing_cancel_trial")}
                </Button>
              )}
              {user?.billing_status === "active" && user?.stripe_subscription_id && !user?.cancel_at_period_end && (
                <Button variant="outline" onClick={() => setCancelSubOpen(true)} disabled={!!billingBusy}
                  data-testid="cancel-sub-btn"
                  className="rounded-sm gap-2 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10">
                  {t("billing_cancel_trial")}
                </Button>
              )}
              {user?.cancel_at_period_end && (
                <Button variant="outline" onClick={resumeSubscription} disabled={!!billingBusy}
                  data-testid="resume-sub-btn" className="rounded-sm gap-2">
                  {t("billing_resume_subscription")}
                </Button>
              )}
              {user?.stripe_customer_id && (
                <Button variant="outline" onClick={openPortal} disabled={billingBusy === "portal"}
                  data-testid="manage-billing-btn" className="rounded-sm gap-2">
                  <CreditCard className="h-4 w-4" /> {t("billing_manage")}
                </Button>
              )}
            </div>
          </div>

          {user?.billing_status === "trialing" && user?.trial_ends_at && (
            <p className="text-sm text-[hsl(var(--success))] mt-4 border border-[hsl(var(--success))]/40 rounded-sm p-3" data-testid="trial-banner">
              {t("billing_trial_banner_until")} {fmtDate(user.trial_ends_at)}. {t("billing_trial_banner_charge")}{" "}
              {fmtCurrency((plans || []).find((p) => p.key === user?.plan)?.price_monthly || 0)}{t("billing_per_month")}{" "}
              {t("billing_trial_banner_unless")}
            </p>
          )}

          {user?.cancel_at_period_end && user?.cancel_at && (
            <p className="text-sm text-muted-foreground mt-4 border border-border rounded-sm p-3" data-testid="cancel-scheduled-banner">
              {t("billing_cancel_scheduled_until")} {fmtDate(user.cancel_at)}. {t("billing_cancel_scheduled_suffix")}
            </p>
          )}

          {user?.billing_status === "past_due" && (
            <p className="text-sm text-[hsl(var(--warning))] mt-4 border border-[hsl(var(--warning))]/40 rounded-sm p-3">
              {t("billing_past_due_warning")}
            </p>
          )}

          {/* A live subscription (trialing/active/past_due) already exists — a new
              Checkout session would just 400 ("already have a subscription"). Plan
              changes on an existing subscription go through the Stripe portal,
              which handles proration correctly; only offer fresh-checkout buttons
              when there's nothing to switch. */}
          {hasActiveSubscription ? (
            <p className="text-sm text-muted-foreground mt-4">{t("billing_change_plan_via_portal")}</p>
          ) : (plans || []).filter((p) => p.price_monthly > 0 && p.key !== user?.plan).length > 0 && (
            <>
              <div className="inline-flex items-center gap-1 mt-4 p-1 rounded-full border border-border bg-secondary/40">
                {["month", "year"].map((iv) => (
                  <button key={iv} onClick={() => setBillingInterval(iv)} data-testid={`settings-interval-${iv}`}
                    className={`px-4 py-1.5 rounded-full text-xs transition-colors ${
                      billingInterval === iv ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
                    }`}>
                    {iv === "month" ? t("billing_interval_monthly") : t("billing_interval_annual")}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {(plans || []).filter((p) => p.price_monthly > 0 && p.key !== user?.plan).map((p) => {
                  const annual = billingInterval === "year";
                  const price = annual ? p.price_yearly : p.price_monthly;
                  if (annual && price == null) return null;
                  return (
                    <Button key={p.key} onClick={() => startCheckout(p.key, billingInterval)} disabled={!!billingBusy}
                      data-testid={`upgrade-${p.key}-btn`} className="rounded-sm gap-2">
                      {user?.trial_used ? t("billing_upgrade") : t("billing_trial_cta")} — {p.name} ({fmtCurrency(price)}{annual ? t("billing_per_year") : t("billing_per_month")})
                    </Button>
                  );
                })}
              </div>
            </>
          )}
        </Card>
      </motion.div>
      )}

      {/* Faturação de Rendas — Stripe Connect onboarding for SEPA auto-billing */}
      {user?.role !== "accountant" && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}>
        <Card className="p-6 rounded-sm border-border" data-testid="rent-billing-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex gap-3">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[hsl(var(--success))]" />
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  {t("rent_billing_title")}
                  {connectStatus?.status && (
                    <Badge variant="outline" className="rounded-sm text-[10px]"
                      style={{ color: CONNECT_STATUS_META[connectStatus.status]?.color,
                              borderColor: CONNECT_STATUS_META[connectStatus.status]?.color }}>
                      {t(CONNECT_STATUS_META[connectStatus.status]?.key)}
                    </Badge>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">{t("rent_billing_desc")}</p>
                <p className="text-xs text-muted-foreground mt-2 max-w-md">
                  {t("rent_billing_fee_note")}{" "}
                  <a href="https://stripe.com/pricing/local-payment-methods" target="_blank" rel="noreferrer"
                    className="underline hover:text-foreground">
                    {t("rent_billing_fee_link")}
                  </a>
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {connectStatus?.status === "active" ? (
                <Badge className="rounded-sm bg-[hsl(var(--success))] text-white gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {t("connect_status_active")}
                </Badge>
              ) : connectStatus && !connectStatus.plan_allows ? (
                <p className="text-xs text-muted-foreground" data-testid="rent-billing-upgrade-note">
                  {t("rent_billing_upgrade_required")}
                </p>
              ) : connectStatus?.configured ? (
                <Button onClick={startConnectOnboarding} disabled={connectBusy} data-testid="connect-stripe-btn"
                  className="rounded-sm gap-2">
                  <Link2 className="h-4 w-4" /> {t("connect_stripe_cta")}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">{t("rent_billing_unconfigured")}</p>
              )}
            </div>
          </div>
        </Card>
      </motion.div>
      )}

      {/* 2FA */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="p-6 rounded-sm border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
                {user?.twofa_enabled ? <ShieldCheck className="h-5 w-5 text-[hsl(var(--success))]" /> : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2">{t("twofa_title")}
                  {user?.twofa_enabled && <Badge className="rounded-sm bg-[hsl(var(--success))] text-white text-[10px]">ON</Badge>}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">{t("twofa_desc")}</p>
              </div>
            </div>
          </div>

          {!user?.twofa_enabled ? (
            <Button onClick={startSetup} disabled={busy} data-testid="enable-2fa-btn" className="rounded-sm gap-2 mt-4">
              <QrCode className="h-4 w-4" /> {t("enable_2fa")}
            </Button>
          ) : (
            <div className="flex flex-wrap items-end gap-2 mt-4">
              <div className="space-y-1">
                <Label className="text-xs">{t("twofa_code")}</Label>
                <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="disable-2fa-code"
                  className="rounded-sm w-40 font-mono" placeholder="000000" maxLength={6} />
              </div>
              <Button variant="destructive" onClick={disable} disabled={busy} data-testid="disable-2fa-btn" className="rounded-sm">
                {t("disable_2fa")}
              </Button>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Dados fiscais — NIF do senhorio, usado para prefill da folha de recibo */}
      {user?.role !== "accountant" && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
        <Card className="p-6 rounded-sm border-border">
          <h3 className="font-medium flex items-center gap-2"><Landmark className="h-4 w-4" /> {t("fiscal_data_title")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("fiscal_data_desc")}</p>
          <div className="flex flex-wrap items-end gap-2 mt-4">
            <div className="space-y-1">
              <Label className="text-xs">{t("nif")}</Label>
              <Input value={nif} onChange={(e) => setNif(e.target.value)} data-testid="settings-nif"
                className="rounded-sm w-48" placeholder="123456789" />
            </div>
            <Button onClick={saveNif} disabled={nifSaving} data-testid="save-nif-btn" className="rounded-sm">
              {nifSaving ? "..." : t("save")}
            </Button>
          </div>
        </Card>
      </motion.div>
      )}

      {/* RGPD */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="p-6 rounded-sm border-border">
          <h3 className="font-medium flex items-center gap-2"><KeyRound className="h-4 w-4" /> {t("privacy_title")}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t("privacy_desc")}</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button variant="outline" onClick={exportData} data-testid="export-data-btn" className="rounded-sm gap-2">
              <Download className="h-4 w-4" /> {t("export_data")}
            </Button>
            <Button variant="outline" onClick={() => setDelOpen(true)} data-testid="delete-account-btn"
              className="rounded-sm gap-2 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10">
              <Trash2 className="h-4 w-4" /> {t("delete_account")}
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* 2FA setup dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("enable_2fa")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{t("twofa_scan")}</p>
            {qr && <img src={qr} alt="QR" className="h-44 w-44 mx-auto rounded-sm bg-white p-2" data-testid="twofa-qr" />}
            <p className="text-xs text-center text-muted-foreground break-all font-mono">{secret}</p>
            <div className="space-y-2">
              <Label>{t("twofa_code")}</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="enable-2fa-code"
                className="rounded-sm text-center tracking-[0.4em] font-mono text-lg" placeholder="000000" maxLength={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSetupOpen(false)} className="rounded-sm">{t("cancel")}</Button>
            <Button onClick={enable} disabled={busy} data-testid="confirm-2fa-btn" className="rounded-sm">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* delete confirm */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[hsl(var(--destructive))]">{t("delete_account")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{t("delete_confirm")}</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)} className="rounded-sm">{t("cancel")}</Button>
            <Button variant="destructive" onClick={deleteAccount} data-testid="confirm-delete-btn" className="rounded-sm">{t("delete_account")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* cancel trial confirm */}
      <AlertDialog open={cancelTrialOpen} onOpenChange={setCancelTrialOpen}>
        <AlertDialogContent className="bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("billing_cancel_trial_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("billing_cancel_trial_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); cancelTrial(); }}
              disabled={billingBusy === "cancel-trial"}
              data-testid="confirm-cancel-trial-btn" className="rounded-sm">
              {t("billing_cancel_trial_confirm_action")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* cancel (at period end) confirm */}
      <AlertDialog open={cancelSubOpen} onOpenChange={setCancelSubOpen}>
        <AlertDialogContent className="bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("billing_cancel_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("billing_cancel_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); cancelSubscription(); }}
              disabled={billingBusy === "cancel-sub"}
              data-testid="confirm-cancel-sub-btn" className="rounded-sm">
              {t("billing_cancel_trial_confirm_action")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
